<template>
  <v-container>
    <v-card>
      <v-card-title>ລາຍງານ AR ຕາມລູກຄ້າ</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              max-width="290"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="asOfDate"
                  label="ວັນທີ (as-of)"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="asOfDate"
                @input="dateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-btn-toggle
              v-model="segment"
              mandatory
            >
              <v-btn value="residential">
                ຄົວເຮືອນ
              </v-btn>
              <v-btn value="commercial">
                ຫົວໜ່ວຍທຸລະກິດ
              </v-btn>
              <v-btn value="both">
                ທັງໝົດ
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <v-btn
          color="primary"
          :loading="submitting"
          @click="onExport"
        >
          Export Excel
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card class="mt-4">
      <v-card-title>
        ບົດລາຍງານທີ່ມີຢູ່ແລ້ວ
        <v-spacer />
        <v-btn
          v-if="selected.length"
          color="error"
          class="mr-2"
          small
          :loading="deleting"
          @click="confirmDelete = true"
        >
          <v-icon
            left
            small
          >
            mdi-delete
          </v-icon>
          ລຶບທີ່ເລືອກ ({{ selected.length }})
        </v-btn>
        <v-btn
          icon
          :loading="loadingRecent"
          title="ໂຫຼດຄືນ"
          @click="fetchRecent"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="allExports"
        :loading="loadingRecent"
        :item-class="rowClass"
        no-data-text="ຍັງບໍ່ມີບົດລາຍງານ"
        :items-per-page="10"
        item-key="id"
        show-select
        dense
      >
        <template #[`item.data-table-select`]="{ isSelected, select, item }">
          <v-simple-checkbox
            :value="isSelected"
            :disabled="!isDeletable(item)"
            :ripple="false"
            @input="select($event)"
          />
        </template>
        <template #[`item.params.as_of_date`]="{ item }">
          {{ item.params && item.params.as_of_date }}
        </template>
        <template #[`item.params.segment`]="{ item }">
          {{ segmentLabel(item.params && item.params.segment) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status)"
            text-color="white"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #[`item.row_count`]="{ item }">
          {{ item.row_count != null ? item.row_count.toLocaleString() : '-' }}
        </template>
        <template #[`item.file_size`]="{ item }">
          {{ formatSize(item.file_size) }}
        </template>
        <template #[`item.finished_at`]="{ item }">
          {{ formatDateTime(item.finished_at) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <v-progress-circular
            v-if="isInProgress(item)"
            indeterminate
            size="20"
            width="2"
            color="primary"
            title="ກຳລັງສ້າງ..."
          />
          <v-btn
            v-else
            icon
            :href="item.download_url"
            :disabled="!item.download_url"
            target="_blank"
            title="ດາວໂຫລດ"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="confirmDelete"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title>ຢືນຢັນການລຶບ</v-card-title>
        <v-card-text>
          ທ່ານຕ້ອງການລຶບ {{ selected.length }} ບົດລາຍງານ ບໍ່?
          ຂໍ້ມູນທີ່ລຶບແລ້ວຈະບໍ່ສາມາດກູ້ຄືນໄດ້.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="deleting"
            @click="confirmDelete = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="deleteSelected"
          >
            ລຶບ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmExport"
      max-width="420"
      persistent
    >
      <v-card>
        <v-card-title>ຢືນຢັນການສົ່ງອອກຊ້ຳ</v-card-title>
        <v-card-text>
          ວັນທີ {{ asOfDate }} ({{ segmentLabel(segment) }}) ມີຢູ່ໃນລາຍການ export ແລ້ວ.
          ທ່ານຕ້ອງການ export ອີກຄັ້ງບໍ?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="submitting"
            @click="confirmExport = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="runExport"
          >
            ສົ່ງອອກ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'ArAsOfDate',
  data() {
    const firstOfMonth = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    })();
    return {
      asOfDate: firstOfMonth,
      segment: 'residential',
      dateMenu: false,
      submitting: false,
      recentExports: [],
      loadingRecent: false,
      selected: [],
      confirmDelete: false,
      confirmExport: false,
      deleting: false,
      headers: [
        { text: 'ວັນທີ (as-of)', value: 'params.as_of_date', sortable: false },
        { text: 'ປະເພດ', value: 'params.segment', sortable: false },
        { text: 'ສະຖານະ', value: 'status', sortable: false },
        {
          text: 'ຈຳນວນແຖວ', value: 'row_count', align: 'end', sortable: false,
        },
        {
          text: 'ຂະໜາດ', value: 'file_size', align: 'end', sortable: false,
        },
        { text: 'ເວລາສຳເລັດ', value: 'finished_at', sortable: false },
        { text: 'ຜູ້ສ້າງ', value: 'generator_name', sortable: false },
        {
          text: '', value: 'actions', sortable: false, align: 'end',
        },
      ],
    };
  },
  computed: {
    ...mapState('exports', ['jobs']),
    // Merge Vuex's in-flight jobs (queued/running/ready) with the fetched list.
    // Vuex jobs win on collision so real-time status updates propagate.
    allExports() {
      const vuexJobs = Object.values(this.jobs || {})
        .filter((j) => j && j.type === 'ar_as_of_date');
      const vuexIds = new Set(vuexJobs.map((j) => j.id));
      const merged = [
        ...vuexJobs,
        ...this.recentExports.filter((j) => !vuexIds.has(j.id)),
      ];
      merged.sort((a, b) => (b.id || 0) - (a.id || 0));
      return merged;
    },
  },
  watch: {
    jobs: {
      deep: true,
      handler(newJobs) {
        const hasReadyAr = Object.values(newJobs).some(
          (j) => j && j.type === 'ar_as_of_date' && j.status === 'ready',
        );
        if (hasReadyAr) {
          this.fetchRecent();
        }
      },
    },
  },
  mounted() {
    this.fetchRecent();
  },
  methods: {
    async onExport() {
      const duplicate = this.recentExports.some(
        (j) => j.params
          && j.params.as_of_date === this.asOfDate
          && j.params.segment === this.segment,
      );
      if (duplicate) {
        this.confirmExport = true;
        return;
      }
      await this.runExport();
    },
    async runExport() {
      this.confirmExport = false;
      this.submitting = true;
      try {
        await this.$store.dispatch('exports/create', {
          type: 'ar_as_of_date',
          params: { as_of_date: this.asOfDate, segment: this.segment },
        });
      } finally {
        this.submitting = false;
      }
    },
    async fetchRecent() {
      this.loadingRecent = true;
      try {
        const res = await this.$axios.get('v2/export-jobs', {
          params: {
            type: 'ar_as_of_date',
            status: 'queued,running,ready',
            limit: 20,
          },
        });
        this.recentExports = (res.data && res.data.data) || [];
      } catch (e) {
        this.recentExports = [];
      } finally {
        this.loadingRecent = false;
      }
    },
    async deleteSelected() {
      this.deleting = true;
      const ids = this.selected.map((j) => j.id);
      try {
        await Promise.all(
          ids.map((id) => this.$axios.delete(`v2/export-jobs/${id}`)),
        );
        // Also drop from Vuex state so the merged list updates immediately.
        ids.forEach((id) => this.$store.dispatch('exports/dismiss', id));
        this.selected = [];
        this.confirmDelete = false;
        await this.fetchRecent();
      } catch (e) {
        // Leave selection intact so user can retry; surface backend error if present.
        this.confirmDelete = false;
      } finally {
        this.deleting = false;
      }
    },
    isInProgress(item) {
      return item.status === 'queued' || item.status === 'running';
    },
    isDeletable(item) {
      return item.status === 'ready' || item.status === 'failed';
    },
    segmentLabel(seg) {
      if (seg === 'residential') return 'ຄົວເຮືອນ';
      if (seg === 'commercial') return 'ຫົວໜ່ວຍທຸລະກິດ';
      if (seg === 'both') return 'ທັງໝົດ';
      return seg || '-';
    },
    statusLabel(status) {
      const map = {
        queued: 'ກຳລັງຈັດຄິວ',
        running: 'ກຳລັງສ້າງ',
        ready: 'ພ້ອມ',
        failed: 'ຜິດພາດ',
      };
      return map[status] || status || '-';
    },
    statusColor(status) {
      if (status === 'ready') return 'success';
      if (status === 'failed') return 'error';
      if (status === 'running') return 'primary';
      return 'grey';
    },
    formatSize(bytes) {
      if (bytes == null) return '-';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    rowClass(item) {
      if (
        item.params
        && item.params.as_of_date === this.asOfDate
        && item.params.segment === this.segment
      ) {
        return 'match-current-form';
      }
      return '';
    },
  },
};
</script>

<style scoped>
.match-current-form {
  background-color: rgba(76, 175, 80, 0.12);
}
</style>
